import { OptionallyUniqueSimulationNode } from "@/Simulation/interfaces"
import { SimulationData } from "@/client-store/simulationView/types"
import { delayBatch } from "@ekkosense/predict-utilities"
import { Interface } from "@ekkosense/predict-performance-calculation"
import { Endpoint } from "../helpers"
import { ProvisioningState } from "../interfaces/ProvisioningState"
import { Temperature } from "../interfaces/Temperature"
import { AnyJSONRPC } from "./AnyJSONRPC"
import { SimulationPayload } from "./SimulationPayload"
import { cacheWrap } from "@ekkosense/ekkosoft-portable-utilities"

/**
 * JSONRPC service for calling the power-simulator service
 */
class PowerSimulatorService extends AnyJSONRPC {
    protected endpoint: Endpoint = "power-simulator"
    protected readonly autoCompressTo = "deflate" as const

    /**
     *
     * @param nodes
     * @param climate
     * @param itLoads
     * @param provisioningStates
     * @returns
     */
    @cacheWrap(300, {formatForUid: ([nodes, climate, itLoads, provisioningStates]) =>
        [nodes.map(n => n.uniqueId ?? n), climate, itLoads, provisioningStates]})
    async simulate(
        nodes: OptionallyUniqueSimulationNode[],
        climate: Interface.TMYData | Temperature,
        itLoads: Record<string, number>,
        provisioningStates: { [ref: string]: ProvisioningState }
    ): Promise<SimulationData> {
        return this.callMethod(
            "simulate",
            {
               nodes,
               climate,
               itLoads,
               provisioningStates
            }
        )
    }

    /**
     *
     * @param nodes
     * @param climate
     * @param itLoads
     * @param provisioningStates
     * @returns
     */
    @cacheWrap(300, {formatForUid: ([nodes, climate, itLoads, provisioningStates]) => [
        nodes.map(n => n.uniqueId ?? n), climate, itLoads, provisioningStates]})
    async simulateBatchPossible(
        nodes: OptionallyUniqueSimulationNode[],
        climate: Interface.TMYData | Temperature,
        itLoads: Record<string, number>,
        provisioningStates: { [ref: string]: ProvisioningState }
    ) {
        const [result] = await this.simulateBatch(
            {
               nodes,
               climate,
               itLoads,
               provisioningStates
            }
        )
        return result
    }

    /**
     *
     * @param payloads
     * @returns
     */
    @delayBatch(50, 24)
    async simulateBatch(...payloads: SimulationPayload[]): Promise<SimulationData[]> {
        let previousNodesSignature: string | undefined
        return this.callMethod(
            "simulate.batch",
            payloads.map(payload => {
                // Note: this has been tested as valid
                const nodesSignature = JSON.stringify(
                    payload.nodes.map(n => n.uniqueId ?? n))
                if(previousNodesSignature === nodesSignature) {
                    return {...payload, nodes: undefined}
                } else {
                    previousNodesSignature = nodesSignature
                    return payload
                }
            })
        )
    }
}

export const powerSimulatorService = new PowerSimulatorService()
